import React from 'react';

import { useIntl } from 'react-intl-next';

import LinkExternalIcon from '@atlaskit/icon/utility/link-external';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
	PrimaryProductKeys,
	type ProductConfig,
	ProductKeys,
} from '../../../../../common/constants/products';
import { useAppContext } from '../../../../../common/ui/app-context';
import { OAuthWarningWrapper } from '../../../../../common/ui/oauth-warning-wrapper';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { isProductSmartlink } from '../../../../../common/utils/oauth-container';
import { onAppFilterSelect as onAppFilterSelectEvent } from '../../../../../common/utils/quick-find/events/app-filter-selected';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../../controllers/oauth';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../../controllers/quick-find/analytics';
import { useBootstrap, useSearchActions } from '../../../../../controllers/store';
import {
	useQuickFindActions,
	useQuickFindQuery,
} from '../../../../../controllers/store/quick-find';

import { i18n } from './messages';
import { useInlineAppSelector } from './utils';

const inlineFilterStyles = xcss({
	display: 'flex',
	gap: 'space.100',
});

const inlineFilterSeparator = xcss({
	width: '1px',
	height: '29px', // As specified in the designs
	alignSelf: 'center',
	borderRight: `1px solid ${token('color.border')}`,
});

const AppButtonFor3PApps = ({
	productKey,
	redirectToFPSWithProductSelected,
	trigger,
}: {
	productKey: ProductKeys;
	productConfig: ProductConfig;
	redirectToFPSWithProductSelected: () => void;
	trigger: (props: { triggerProps?: TriggerProps; userNeedsAuth: boolean }) => JSX.Element;
}) => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const isSmartlink = isProductSmartlink({ productKey, thirdPartyConfigs });
	const { userNeedsOAuth } = useIsUserOAuthed(productKey);
	const quickFindQuery = useQuickFindQuery();

	if (userNeedsOAuth) {
		return (
			<ErrorBoundary>
				<OAuthWarningWrapper
					key={productKey}
					product={productKey}
					postAuthAction={() => {
						redirectToFPSWithProductSelected();
					}}
					trigger={(triggerProps) => trigger({ ...triggerProps, userNeedsAuth: true })}
					isOnSearchDialog={true}
					useOAuthHandlers={useOAuthHandlers}
					useIsUserOAuthed={useIsUserOAuthed}
					isSmartlink={isSmartlink}
					searchQuery={quickFindQuery}
				/>
			</ErrorBoundary>
		);
	}

	return trigger({ userNeedsAuth: false });
};

// Refer to the folowing designs for the Quick Find Dialog responsiveness
// https://www.figma.com/design/50bR8aLZ4eCuXlkrejuCiw/Q3-FY25-Enterprise-Search-Design?node-id=1576-34214&t=GOmwvwnkhe6CCgJs-0
export const InlineAppSelector = () => {
	const { formatMessage } = useIntl();
	const { onNavigate, generateSearchUrl, queryParams } = useAppContext();
	const { setQuickFindOpen } = useQuickFindActions();
	const quickFindQuery = useQuickFindQuery();
	const { selectProductFilter } = useSearchActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const {
		apps1PToDisplay,
		apps3PToDisplay,
		isDialogAbove400px,
		isDialogAbove450px,
		primaryProduct,
		selectedProduct,
	} = useInlineAppSelector();

	const onAppFilterSelected = (
		productKey: ProductKeys,
		options: { redirectToFPS: boolean; isPostAuthAction: boolean },
	) => {
		const newSelectedProduct = selectedProduct === productKey ? undefined : productKey;

		fireAnalyticsEvent(
			onAppFilterSelectEvent({
				actionSubjectId: productKey,
				action: newSelectedProduct === undefined ? 'unselected' : 'selected',
				attributes: {
					...commonAttributes,
					isPostAuthAction: options.isPostAuthAction,
					isRedirectedToFPS: options.redirectToFPS,
				},
				nonPrivacySafeAttributes,
			}),
		);

		if (!options.redirectToFPS) {
			selectProductFilter(newSelectedProduct);
			return;
		}

		const onNavigateCallback = typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

		onNavigateCallback(
			generateSearchUrl({
				...queryParams,
				product: productKey,
				text: quickFindQuery,
			}),
			'push',
		);

		setQuickFindOpen(false);
	};

	const apps3pComponent = apps3PToDisplay.map(({ productKey, productConfig }) => {
		const Icon = productConfig.Icon;
		return (
			<Tooltip
				content={formatMessage(i18n.appTooltipFor3PApps, {
					productName: productConfig.displayName,
				})}
				key={productKey}
			>
				<AppButtonFor3PApps
					key={productKey}
					productKey={productKey}
					productConfig={productConfig}
					redirectToFPSWithProductSelected={() =>
						onAppFilterSelected(productKey, { redirectToFPS: true, isPostAuthAction: true })
					}
					trigger={({ triggerProps, userNeedsAuth }) => (
						<FilterButton
							{...triggerProps}
							contentFit={isDialogAbove450px ? 'default' : 'compact'}
							iconAfter={
								!userNeedsAuth && (
									<LinkExternalIcon
										label={formatMessage(i18n.externalLinkIconLabel)}
										color="currentColor"
									/>
								)
							}
							iconBefore={<Icon size="small" />}
							isSelected={false}
							onClick={() => {
								if (!userNeedsAuth) {
									onAppFilterSelected(productKey, {
										redirectToFPS: true,
										isPostAuthAction: false,
									});
								}
							}}
							spacing={!userNeedsAuth || isDialogAbove400px ? 'default' : 'compact'}
						>
							{isDialogAbove400px && productConfig.displayName}
						</FilterButton>
					)}
				/>
			</Tooltip>
		);
	});

	const apps1PComponent = apps1PToDisplay.map(({ productKey, productConfig, redirectToFPS }) => {
		const AppIcon = productConfig.Icon;

		return (
			<FilterButton
				key={productKey}
				aria-current={selectedProduct === productKey}
				contentFit={isDialogAbove450px ? 'default' : 'compact'}
				iconAfter={
					redirectToFPS && (
						<LinkExternalIcon
							label={formatMessage(i18n.externalLinkIconLabel)}
							color="currentColor"
						/>
					)
				}
				iconBefore={<AppIcon size="small" />}
				isSelected={selectedProduct === productKey}
				onClick={() =>
					onAppFilterSelected(productKey, {
						redirectToFPS,
						isPostAuthAction: false,
					})
				}
				spacing={isDialogAbove400px ? 'default' : 'compact'}
			>
				{isDialogAbove400px && productConfig.displayName}
			</FilterButton>
		);
	});

	return (
		<Box testId="qf-inline-app-selector" xcss={inlineFilterStyles}>
			{primaryProduct === PrimaryProductKeys.Confluence && (
				<>
					{apps1PComponent}
					{apps3pComponent}
				</>
			)}

			{primaryProduct !== PrimaryProductKeys.Confluence && (
				<>
					{apps3pComponent}
					{apps1PComponent}
				</>
			)}

			{selectedProduct && <Box xcss={inlineFilterSeparator} />}
		</Box>
	);
};
